import { useEffect } from 'react'

import { useAuthStore } from '@/stores/authStore'
import { useUiStore } from '@/stores/uiStore'

const DARK_THEME_COLOR = '#17121d'

/**
 * Mirrors the theme preference onto `<html data-theme>` (which the token
 * stylesheet keys off) and onto the address-bar colour on mobile. Dark is an
 * explicit choice only — `system` means light, as the design intends. The
 * light address-bar colour follows the brand (`useApplyBrand` must run first,
 * so this reads what it wrote onto `:root`).
 */
export function useApplyTheme(): void {
  const theme = useUiStore((state) => state.theme)
  const brandColor = useAuthStore((state) => state.session?.workspace?.brandColor ?? null)

  useEffect(() => {
    const root = document.documentElement

    if (theme === 'system') {
      delete root.dataset.theme
    } else {
      root.dataset.theme = theme
    }

    // `system` resolves to light: the design has no OS-driven dark mode.
    const light = getComputedStyle(root).getPropertyValue('--palette-brand-700').trim()
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', theme === 'dark' ? DARK_THEME_COLOR : light)
  }, [theme, brandColor])
}
